import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import MessageBox from "../../message-box";
import {
  cancelVoiceRecording,
  getVoiceRecognitionResult,
  startVoiceRecording,
  stopVoiceRecording,
} from "../voice/voiceBridge";
import {
  ensureVoiceCapability,
  invalidateVoiceCapability,
  prepareVoiceCapability,
  subscribeVoiceCapabilityLifecycle,
} from "../voice/voiceCapabilityCoordinator";
import {
  activateVoiceSession,
  queueVoiceSessionRecovery,
  releaseVoiceSession,
  waitForVoiceSessionRecovery,
} from "../voice/voiceSessionCoordinator";

const MAX_VOICE_RECORDING_DURATION_MS = 60_000;

type VoicePhase = "editing" | "recording" | "recognizing";

/** 结束录音的触发来源：手动结束、录音超时、切换到其他语音输入。 */
type FinishRecordingReason = "manual" | "timeout" | "sessionSwitch";

interface RecognitionOptions {
  /** 空识别结果是否静默（超时已有独立提示，避免 Toast 重叠） */
  suppressEmptyResultNotice: boolean;
  /** 回填文本后是否恢复焦点与光标（自动结束时不弹起键盘） */
  shouldRestoreFocus: boolean;
}

interface UseVoiceToTextOptions {
  enabled: boolean;
  value: string;
  maxLength: number;
  textareaRef: RefObject<HTMLTextAreaElement>;
  onChange?: (value: string) => void;
  onNotice: (message: string) => void;
}

/**
 * 管理语音转文字的原生交互与结果回填，不包含按钮、图标等展示逻辑。
 */
export function useVoiceToText({
  enabled,
  value,
  maxLength,
  textareaRef,
  onChange,
  onNotice,
}: UseVoiceToTextOptions) {
  const contentRef = useRef(value || "");
  const selectionRef = useRef({ start: 0, end: 0 });
  const recordStartPromiseRef = useRef<Promise<any> | null>(null);
  const finishPromiseRef = useRef<Promise<void> | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const voiceSessionIdRef = useRef(Symbol("textarea-voice-session"));
  const phaseRef = useRef<VoicePhase>("editing");
  const isMountedRef = useRef(false);
  const recordingAttemptRef = useRef(0);
  const [phase, setPhase] = useState<VoicePhase>("editing");
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  const clearAutoStopTimer = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      recordingAttemptRef.current += 1;
      clearAutoStopTimer();
      releaseVoiceSession(voiceSessionIdRef.current);
      if (phaseRef.current === "recording") {
        void queueVoiceSessionRecovery(() => cancelVoiceRecording());
      }
    };
  }, [clearAutoStopTimer]);

  useEffect(() => {
    if (!enabled) return;

    let isWarmupCancelled = false;
    const unsubscribe = subscribeVoiceCapabilityLifecycle();
    const warmupTimer = window.setTimeout(() => {
      void waitForVoiceSessionRecovery()
        .then(() => {
          if (
            isWarmupCancelled ||
            document.visibilityState === "hidden"
          ) {
            return false;
          }
          return prepareVoiceCapability();
        })
        .catch(() => undefined);
    }, 0);

    return () => {
      isWarmupCancelled = true;
      window.clearTimeout(warmupTimer);
      unsubscribe();
    };
  }, [enabled]);

  const isActiveAttempt = useCallback((attemptId: number) => {
    return isMountedRef.current && recordingAttemptRef.current === attemptId;
  }, []);

  const updatePhase = useCallback((nextPhase: VoicePhase) => {
    phaseRef.current = nextPhase;
    if (isMountedRef.current) {
      setPhase(nextPhase);
    }
  }, []);

  const updateRequestingPermission = useCallback((isRequesting: boolean) => {
    if (isMountedRef.current) {
      setIsRequestingPermission(isRequesting);
    }
  }, []);

  useEffect(() => {
    contentRef.current = value || "";
  }, [value]);

  const resetRecordingState = useCallback(() => {
    clearAutoStopTimer();
    recordStartPromiseRef.current = null;
    releaseVoiceSession(voiceSessionIdRef.current);
    updatePhase("editing");
  }, [clearAutoStopTimer, updatePhase]);

  const forceRecoverVoiceSession = useCallback(
    (notice?: string) => {
      const shouldCancelNative =
        phaseRef.current === "recording" ||
        phaseRef.current === "recognizing" ||
        recordStartPromiseRef.current !== null;

      // 先废弃当前 attempt，确保超时后到达的原生回调不会污染下一次录音。
      recordingAttemptRef.current += 1;
      clearAutoStopTimer();
      recordStartPromiseRef.current = null;
      finishPromiseRef.current = null;
      releaseVoiceSession(voiceSessionIdRef.current);
      invalidateVoiceCapability();
      updateRequestingPermission(false);
      updatePhase("editing");

      // cancel 纳入全局恢复队列；H5 状态立即复位，但下一次 start 必须等待它结束。
      const recoveryPromise = shouldCancelNative
        ? queueVoiceSessionRecovery(() => cancelVoiceRecording())
        : Promise.resolve();
      if (notice) {
        onNotice(notice);
      }
      return recoveryPromise;
    },
    [clearAutoStopTimer, onNotice, updatePhase, updateRequestingPermission]
  );

  const cancelForPageHidden = useCallback(() => {
    void forceRecoverVoiceSession();
  }, [forceRecoverVoiceSession]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        cancelForPageHidden();
      }
    };
    const handlePageHide = () => cancelForPageHidden();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [cancelForPageHidden]);

  const restoreRecognitionFocus = useCallback(
    (caretPosition: number) => {
      window.requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.focus();
        textarea.setSelectionRange(caretPosition, caretPosition);
        textarea.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    [textareaRef]
  );

  const appendRecognitionResult = useCallback(
    (result: string, shouldRestoreFocus: boolean) => {
      if (!isMountedRef.current) return;
      const currentValue = contentRef.current;
      const start = Math.min(selectionRef.current.start, currentValue.length);
      const end = Math.min(
        Math.max(selectionRef.current.end, start),
        currentValue.length
      );
      const prefix = currentValue.slice(0, start);
      const suffix = currentValue.slice(end);
      const availableLength = Math.max(
        0,
        maxLength - prefix.length - suffix.length
      );
      const text = result.slice(0, availableLength);

      if (!text) {
        onNotice("备注字数已达上限");
        return;
      }
      onChange?.(`${prefix}${text}${suffix}`);
      if (shouldRestoreFocus) {
        restoreRecognitionFocus(prefix.length + text.length);
      }
    },
    [maxLength, onChange, onNotice, restoreRecognitionFocus]
  );

  const recognizeRecording = useCallback(
    async (
      requestId: string,
      attemptId: number,
      { suppressEmptyResultNotice, shouldRestoreFocus }: RecognitionOptions
    ) => {
      if (!isActiveAttempt(attemptId)) return;
      updatePhase("recognizing");
      try {
        const result = await getVoiceRecognitionResult(requestId);
        if (!isActiveAttempt(attemptId)) return;
        if (result?.code === 0 && result?.result) {
          appendRecognitionResult(String(result.result), shouldRestoreFocus);
        } else if (result?.code !== 0) {
          await forceRecoverVoiceSession("语音识别失败，请稍后重试");
        } else if (!suppressEmptyResultNotice) {
          onNotice("未识别到文字，请尝试清晰地重复一遍");
        }
      } catch (_error) {
        if (isActiveAttempt(attemptId)) {
          await forceRecoverVoiceSession("语音识别失败，请稍后重试");
        }
      } finally {
        if (isActiveAttempt(attemptId)) {
          resetRecordingState();
        }
      }
    },
    [
      appendRecognitionResult,
      forceRecoverVoiceSession,
      isActiveAttempt,
      onNotice,
      resetRecordingState,
      updatePhase,
    ]
  );

  const ensureVoicePermission = useCallback(
    async (attemptId: number) => {
      if (!isActiveAttempt(attemptId)) return false;
      updateRequestingPermission(true);
      try {
        const isPermissionGranted = await ensureVoiceCapability();
        if (!isActiveAttempt(attemptId)) return false;
        if (!isPermissionGranted) {
          onNotice("麦克风权限未开启，请在系统设置中开启后重试");
          return false;
        }
        return true;
      } catch (_error) {
        if (isActiveAttempt(attemptId)) {
          await forceRecoverVoiceSession("当前环境暂不支持语音输入");
        }
        return false;
      } finally {
        if (isActiveAttempt(attemptId)) {
          updateRequestingPermission(false);
        }
      }
    },
    [
      forceRecoverVoiceSession,
      isActiveAttempt,
      onNotice,
      updateRequestingPermission,
    ]
  );

  const finishRecording = useCallback(
    async (reason: FinishRecordingReason = "manual") => {
      if (finishPromiseRef.current) {
        await finishPromiseRef.current;
        return;
      }
      if (phaseRef.current !== "recording") return;

      const attemptId = recordingAttemptRef.current;
      if (!isActiveAttempt(attemptId)) return;
      clearAutoStopTimer();
      const recoveryNotice =
        reason === "timeout" ? undefined : "录音结束失败，请稍后重试";

      const finishPromise = (async () => {
        const startPromise = recordStartPromiseRef.current;
        try {
          const startResult = startPromise ? await startPromise : null;
          if (!isActiveAttempt(attemptId)) return;
          if (startResult?.code !== 0 || !startResult?.requestId) {
            await forceRecoverVoiceSession(recoveryNotice);
            return;
          }
          const stopResult = await stopVoiceRecording();
          if (!isActiveAttempt(attemptId)) return;
          if (stopResult?.code !== undefined && stopResult.code !== 0) {
            await forceRecoverVoiceSession(recoveryNotice);
            return;
          }
          await recognizeRecording(startResult.requestId, attemptId, {
            // 超时已有独立提示，空识别结果不再重复提示
            suppressEmptyResultNotice: reason === "timeout",
            // 仅用户主动结束时恢复焦点与滚动，自动结束不弹起键盘
            shouldRestoreFocus: reason === "manual",
          });
        } catch (_error) {
          if (isActiveAttempt(attemptId)) {
            await forceRecoverVoiceSession(recoveryNotice);
          }
        }
      })();

      finishPromiseRef.current = finishPromise;
      try {
        await finishPromise;
      } finally {
        if (finishPromiseRef.current === finishPromise) {
          finishPromiseRef.current = null;
        }
      }
    },
    [
      clearAutoStopTimer,
      forceRecoverVoiceSession,
      isActiveAttempt,
      recognizeRecording,
    ]
  );

  const startRecording = useCallback(async () => {
    if (!enabled || !isMountedRef.current || phaseRef.current !== "editing") {
      releaseVoiceSession(voiceSessionIdRef.current);
      return;
    }
    const attemptId = recordingAttemptRef.current + 1;
    recordingAttemptRef.current = attemptId;
    const isPermissionGranted = await ensureVoicePermission(attemptId);
    if (
      !isPermissionGranted ||
      !isActiveAttempt(attemptId) ||
      phaseRef.current !== "editing"
    ) {
      resetRecordingState();
      return;
    }

    const textarea = textareaRef.current;
    const currentValue = contentRef.current;
    selectionRef.current = {
      start: textarea?.selectionStart ?? currentValue.length,
      end: textarea?.selectionEnd ?? currentValue.length,
    };
    textarea?.blur();
    updatePhase("recording");

    try {
      const startPromise = startVoiceRecording();
      recordStartPromiseRef.current = startPromise;
      autoStopTimerRef.current = setTimeout(() => {
        void finishRecording("timeout");
        MessageBox.toast("录音时长已达60秒，已自动结束");
      }, MAX_VOICE_RECORDING_DURATION_MS);
      const result = await startPromise;
      if (!isActiveAttempt(attemptId)) return;
      if (result?.code !== 0) {
        await forceRecoverVoiceSession("录音启动失败，请稍后重试");
      }
    } catch (_error) {
      if (isActiveAttempt(attemptId)) {
        await forceRecoverVoiceSession("录音启动失败，请稍后重试");
      }
    }
  }, [
    enabled,
    ensureVoicePermission,
    finishRecording,
    forceRecoverVoiceSession,
    isActiveAttempt,
    resetRecordingState,
    textareaRef,
    updatePhase,
  ]);

  const finishActiveVoiceSession = useCallback(async () => {
    if (finishPromiseRef.current) {
      await finishPromiseRef.current;
      return;
    }
    if (phaseRef.current === "recording") {
      await finishRecording("sessionSwitch");
    }
  }, [finishRecording]);

  const toggleRecording = useCallback(() => {
    if (phaseRef.current === "editing") {
      void activateVoiceSession({
        id: voiceSessionIdRef.current,
        finish: finishActiveVoiceSession,
        start: startRecording,
      });
    } else if (phaseRef.current === "recording") {
      void finishRecording("manual");
    }
  }, [finishActiveVoiceSession, finishRecording, startRecording]);

  return {
    phase,
    isRequestingPermission,
    isTextLocked: phase !== "editing",
    toggleRecording,
  };
}
